import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { IconButton } from "@mui/material";
import { format } from "date-fns";
import { TableColumn } from "react-data-table-component";

import { CallPlanInfoFragment } from "@/graphql/graphql";
import { CALENDAR_DATE_FORMAT, transformDateUTC } from "@/utils/date-utils";

import { StatusChip } from "../Playlists/components/StatusChip";

/**
 * When two or more plans share the same program and week (e.g. after splitting an active plan),
 * hide edit on the chronologically earliest row so only the newer duplicate stays editable.
 */
export const duplicateProgramWeekEarlierPlanIds = (plans: CallPlanInfoFragment[]): number[] => {
  const n = CALENDAR_DATE_FORMAT.length;

  const byProgramAndWeek = plans.reduce<
    Record<string, { count: number; earliest: CallPlanInfoFragment }>
  >((acc, p) => {
    const key = `${p.program.id}:${p.programWeek}`;
    const bucket = acc[key];
    if (!bucket) {
      acc[key] = { count: 1, earliest: p };
      return acc;
    }
    bucket.count++;
    const cmp = p.startDate.slice(0, n).localeCompare(bucket.earliest.startDate.slice(0, n));
    if (cmp < 0 || (cmp === 0 && p.id < bucket.earliest.id)) {
      bucket.earliest = p;
    }
    return acc;
  }, {});

  return Object.values(byProgramAndWeek).flatMap(({ count, earliest }) =>
    count >= 2 ? [earliest.id] : []
  );
};

export const callPlanTableColumnsBase: TableColumn<CallPlanInfoFragment>[] = [
  {
    name: "Round",
    selector: (row) => row.program?.round ?? "-",
  },
  {
    name: "Week",
    selector: (row) => row.programWeek,
  },
  {
    id: "dateRange",
    name: "Start & End Date",
    selector: (row) => {
      const start = transformDateUTC(row.startDate?.toString());
      const end = row.endDate ? transformDateUTC(row.endDate.toString()) : "Active";
      return start ? `${start} - ${end}` : "-";
    },
    sortField: "endDate",
    sortable: true,
  },
  {
    name: "Activity Count",
    selector: (row) =>
      row.interactions?.filter((interaction) => interaction.__typename === "ActivityInteraction")
        .length ?? 0,
  },
  {
    name: "Status",
    cell: (row) => <StatusChip isActive={row.isActive} />,
  },
];

/** Single source for whether edit or delete is allowed and why it is blocked (for tooltips). */
export interface CallPlanEditOrDeleteState {
  disabled: boolean;
  disabledTooltip?: string;
}

export const getCallPlanEditState = (
  row: CallPlanInfoFragment,
  duplicateWeekEarlierPlanIds: number[],
  hasNoActiveProgram: boolean
): CallPlanEditOrDeleteState => {
  if (hasNoActiveProgram) {
    return {
      disabled: true,
      disabledTooltip: "Cannot edit call plan from a finished program.",
    };
  }

  const today = format(new Date(), CALENDAR_DATE_FORMAT);
  if (
    (row.endDate && row.endDate.slice(0, 10) < today) ||
    duplicateWeekEarlierPlanIds.includes(row.id)
  ) {
    return {
      disabled: true,
      disabledTooltip: "Cannot edit call plan that has already finished.",
    };
  }

  return { disabled: false };
};

export const getCallPlanDeleteState = (
  callPlan: CallPlanInfoFragment,
  duplicateWeekEarlierPlanIds: number[]
): CallPlanEditOrDeleteState => {
  if (duplicateWeekEarlierPlanIds.includes(callPlan.id)) {
    return {
      disabled: true,
      disabledTooltip: "Cannot delete this call plan.",
    };
  }

  const today = format(new Date(), CALENDAR_DATE_FORMAT);
  if (today >= callPlan.startDate.slice(0, 10)) {
    return {
      disabled: true,
      disabledTooltip: "Cannot delete a call plan that has already started",
    };
  }

  return { disabled: false };
};

/** Past plans (ended before today) are read-only; current and future rows show edit. */
export const buildCallPlanColumns = (
  onEditCallPlan: (row: CallPlanInfoFragment) => void,
  duplicateWeekEarlierPlanIds: number[] = [],
  hasNoActiveProgram = false
): TableColumn<CallPlanInfoFragment>[] => {
  return [
    ...callPlanTableColumnsBase,
    {
      name: "",
      width: "52px",
      right: true,
      ignoreRowClick: true,
      button: true,
      cell: (row) => {
        if (getCallPlanEditState(row, duplicateWeekEarlierPlanIds, hasNoActiveProgram).disabled) {
          return null;
        }

        return (
          <IconButton
            size="small"
            aria-label="Edit call plan"
            onClick={() => onEditCallPlan(row)}
            edge="end"
          >
            <EditOutlinedIcon fontSize="small" />
          </IconButton>
        );
      },
    },
  ];
};
